import { useModalAction } from '@components/common/modal/modal.context'
import Counter from '@components/ui/counter'
import Image from '@components/ui/image'
import {
  CONFIGURABLE_PRODUCT_TYPE,
  FLAGS,
  LOCAL_INV_FLAG,
} from '@constants/index'
import { useCart } from '@contexts/cart/cart.context'
import { useCheckout } from '@contexts/checkout/checkout.context'
import { UserContext } from '@contexts/user/user.context'
import { IUser } from '@contexts/user/user.utils'
import { generateCartItem } from '@utils/generate-cart-item'
import useWindowSize from '@utils/use-window-size'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useContext, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import AddToCartButton from './add-to-cart-button'
import AfterpayMessage from './afterpay-message'
import ProductAddToList from './product-addtolist'
import ProductBrand from './product-brand'
import ProductBuyNow from './product-buynow'
import CasesSection from './product-cases-section'
import ProductClickCollect from './product-clickcollect'
import ProductCompare from './product-compare'
import MinSaleBox from './product-min-qty-box'
import ProductOosSection from './product-oos-section'
import ProductPrice from './product-price'
import ShipsFrozenNoReturn from './product-shipsfrozen-noreturn'
import SoldBy from './product-sold-by'
import ProductTags from './product-tags'
import ProductType from './product-type'
import VariantsSection from './product-variants-section'
import ProductRebates from './product-rebate'
import ShippingMessage from './shipping-message'
import ExpectedDelivery from './product-delivery-dates'
import ProductSpecialOrderText from './product-special-order-text'
import ProductLargeOrders from './product-large-orders-text'

export default function ProductAside({
  initialProduct,
  product,
  data,
  item,
  isSelected,
  selectedVariation,
  getUpdatedProduct,
  selectedOptions,
  setSelectedOptions,
  setSelectedSku,
  setVisibleSections,
  selectedSku,
  visibleSections,
  toggleSectionVisibility,
  expectedDelivery,
  onQuantityChange,
  selectedQuantity,
  isShowSpecialOrderDesc,
  setIsShowSpecialOrderDesc,
}: any) {
  const { t: translate } = useTranslation('common')
  const [quantity, setQuantity] = useState(selectedQuantity)
  const [kingstonClicked, setKingstonClicked] = useState(false)
  const { addItemToCart, isInCart, getItemFromCart, isInStock, items } =
    useCart()
  const { user, isHomeBuyer, addToCartLoader } = useContext(
    UserContext,
  ) as IUser
  const isRebateEligibleFlag =
    !isHomeBuyer && data?.flags.includes(FLAGS.REBATE_ELIGIBLE)
  const { magConfig } = useCheckout()

  const router = useRouter()
  const { locale } = router
  function addToCart() {
    if (!isSelected) return
    if (data?.min_sale_qty && quantity < data?.min_sale_qty) {
      if (!items.find((item) => item.id === data.sku)) {
        toast.error(
          `${translate('text-fewest-you-may-purchase')} ${data?.min_sale_qty}`,
          {
            className: 'toast-error',
          },
        )
        return
      }
    }

    const item = generateCartItem(data!, selectedVariation)
    const updatedItem = {
      ...item,
      isClickCollect: kingstonClicked,
    }

    addItemToCart(updatedItem, quantity, selectedOptions)
  }
  const updateQuantityInParent = (newQuantity: number) => {
    setQuantity(newQuantity)
    onQuantityChange(newQuantity) // Send updated quantity to parent
  }
  const inputChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    let numberFlag = isNaN(parseInt(event.target.value))
    let newValue = numberFlag ? 1 : parseInt(event.target.value)
    if (newValue < 1) return false
    updateQuantityInParent(newValue)
  }

  useEffect(() => {
    setQuantity(1)
  }, [data])
  useEffect(() => {
    updateQuantityInParent(selectedQuantity)
  }, [selectedQuantity])

  useEffect(() => {
    if (selectedSku) {
      setIsShowSpecialOrderDesc(false)
    }
  }, [selectedSku])

  function CasesCustomQoute() {
    const { t } = useTranslation('common')
    const { id, quantity, product_type } = data ?? {}
    const { width } = useWindowSize()
    const { openModal } = useModalAction()
    const { isInCart, isInStock } = useCart()
    const iconSize = width! > 1024 ? '19' : '17'
    const outOfStock = isInCart(id) && !isInStock(id)
    const handlePopupView = () => {
      openModal('CUSTOM_QUOTE', data)
    }
    return (
      <>
        <div className="pb-2 font-medium text-[16px]">
          <span>{translate('text-looking-bulk')}</span>
        </div>
        <div className="flex items-center">
          <Image
            src="/assets/images/tag.svg"
            alt="custom-quote-icon"
            width={10}
            height={10}
            className="font-small w-6 h-6"
            style={{ width: '1em', height: '1em' }}
          />
          <a
            onClick={handlePopupView}
            className="cursor-pointer font-semibold focus:outline-none ltr:ml-2 hover:underline text-brand-green text-[16px]"
          >
            {translate('text-get-quote')}
          </a>
        </div>
      </>
    )
  }

  const showAddToList = process.env.NEXT_PUBLIC_SHOW_ADD_TO_LIST === 'true'
  const showCompare = process.env.NEXT_PUBLIC_SHOW_COMPARE === 'true'
  const showBuyNow = process.env.NEXT_PUBLIC_SHOW_BUY_NOW === 'true'
  const showVarianceOptions =
    process.env.NEXT_PUBLIC_SHOW_VARIANCE_OPTIONS === 'true'

  return (
    <>
      <div className="border">
        <SoldBy />
        <div className="p-3">
          <div className="hidden lg:block">
            {data?.is_in_stock && (
              <ProductTags selectedSku={selectedSku} data={data} />
            )}{' '}
          </div>
          <ProductPrice data={data} />
          {data?.is_in_stock && (
            <ShippingMessage selectedSku={selectedSku} data={data} />
          )}
          {data?.is_in_stock && (
            <ProductType
              showZipChange={true}
              expectedDelivery={expectedDelivery}
              data={data}
            />
          )}
          {!data?.is_in_stock && <ProductOosSection sku={data?.sku} />}
          <div className="flex mt-2">
            {data?.is_in_stock && data?.flags.includes(LOCAL_INV_FLAG) && (
              <span className="text-md lg:text-15px font-semibold text-brand-green">
                {translate('text-local-kingston-inventory')}
              </span>
            )}
          </div>
          {data?.is_in_stock &&
            data?.flags.includes('click_collect') &&
            magConfig?.settings?.clickAndCollectDetails
              ?.showClickAndCollect && (
              <ProductClickCollect
                kingstonClicked={kingstonClicked}
                setKingstonClicked={setKingstonClicked}
                page="pdp"
              />
            )}
          <MinSaleBox minSaleQty={data?.min_sale_qty} />
          {data?.is_in_stock && (
            <ExpectedDelivery deliveryBy={expectedDelivery} icon={true} />
          )}
          {data?.is_in_stock && (
            <ProductLargeOrders deliveryBy={expectedDelivery} />
          )}
          {isShowSpecialOrderDesc && <ProductSpecialOrderText />}
          {data?.is_in_stock && (
            <div className="mt-5 flex">
              <Counter
                variant="single"
                value={quantity}
                onIncrement={() => updateQuantityInParent(quantity + 1)}
                onDecrement={() =>
                  updateQuantityInParent(quantity > 1 ? quantity - 1 : 1)
                }
                onInputChange={inputChangeHandler}
                disabled={
                  isInCart(item.id)
                    ? getItemFromCart(item.id).quantity + quantity >=
                      Number(item.stock)
                    : quantity >= Number(item.stock)
                }
                max_sale_qty={data?.max_sale_qty}
              />
              <AddToCartButton
                isShowSpecialOrderDesc={isShowSpecialOrderDesc}
                setIsShowSpecialOrderDesc={setIsShowSpecialOrderDesc}
                addToCart={addToCart}
                addToCartLoader={addToCartLoader}
                isSelected={isSelected}
                data={data}
              />
            </div>
          )}
          <AfterpayMessage data={data} />
          <div
            className={`mt-5 flex items-center justify-end ${
              data?.is_in_stock && 'flex-wrap xs:flex-nowrap'
            } ${showAddToList && user && 'justify-between'}  `}
          >
            {showAddToList && user && (
              <ProductAddToList data={data} addtocartQty={quantity} />
            )}
            {showCompare && <ProductCompare />}
            {showBuyNow && <ProductBuyNow data={data} quantity={quantity} />}
          </div>
        </div>
      </div>
      <div className="border my-5 py-6 p-4 text-xs sm:text-sm">
        <CasesSection data={data} selectedSku={selectedSku} />
        {isRebateEligibleFlag && <ProductRebates data={data} />}
        <CasesCustomQoute />
      </div>
      {showVarianceOptions &&
        (data?.product_type === CONFIGURABLE_PRODUCT_TYPE ||
          initialProduct?.product_type === CONFIGURABLE_PRODUCT_TYPE) && (
          <VariantsSection
            data={data}
            setIsShowSpecialOrderDesc={setIsShowSpecialOrderDesc}
            toggleSectionVisibility={toggleSectionVisibility}
            getUpdatedProduct={getUpdatedProduct}
            selectedOptions={selectedOptions}
            setSelectedOptions={setSelectedOptions}
            setSelectedSku={setSelectedSku}
            setVisibleSections={setVisibleSections}
            selectedSku={selectedSku}
            visibleSections={visibleSections}
          />
        )}
      <ShipsFrozenNoReturn data={data} />
      <ProductBrand data={data} />
    </>
  )
}
